import PropTypes from 'prop-types';
import styled from 'styled-components';

import { config } from '_config';
import { theme } from '_constants/theme';

import * as Layout from '_components/atomic/layouts';
import JoinBanner from '../JoinBanner';

export const howToChooseBestStateToIncorporateBusinessContents = [
  'Why the State Matters',
  'Key Factors to Consider',
  'Popular States for Incorporation',
  'Step to Incorporate in Your Chosen State',
];

const StyledContainer = styled.div`
  position: relative;
`;

const StyledBlock = styled.div`
  position: relative;
  z-index: 1;

  ol,
  ul {
    padding-top: ${theme.spaces.regular}px;

    li {
      margin-bottom: ${theme.spaces.regular}px;

      &::marker {
        font-weight: 700;
      }
    }
  }
`;

const StyledBackgroundImageContainer = styled.div`
  position: absolute;
  top: 40%;
  width: 100%;

  img {
    width: 100%;
  }
`;

const StyledImageContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${theme.spaces.medium}px;
`;

const HowToChooseBestStateToIncorporateBusinessArticle = ({ idPrefix }) => {
  return (
    <StyledContainer>
      <Layout.Card.Text>
        Incorporating your business is a significant step that can offer
        numerous benefits, including legal protection and potential tax
        advantages. However, one of the first decisions you&apos;ll need to make
        is choosing the best state to incorporate your business. This guide will
        help you understand the key factors to consider and the benefits of
        incorporating in different states.
      </Layout.Card.Text>
      <StyledBlock id={`${idPrefix}-${0}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Why the State Matters
        </Layout.Card.Text>
        <Layout.Card.Text>
          The state in which you incorporate can impact several aspects of your
          business, including:
          <ul>
            <li>
              Legal Protections: Different states offer varying levels of legal
              protection and benefits to corporations and LLCs.
            </li>
            <li>
              Tax Implications: State taxes, including income tax and franchise
              tax, can vary widely and affect your overall tax burden.
            </li>
            <li>
              Costs: The cost of incorporating, including filing fees and
              ongoing compliance costs, differs from state to state.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <Layout.Divider />
      <StyledBlock id={`${idPrefix}-${1}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Key Factors to Consider
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          1. Business Location
        </Layout.Card.Text>
        <Layout.Card.Text>
          If your business operates primarily in one state, it often makes sense
          to incorporate there. This simplifies compliance and avoids the need
          for foreign qualification (registering to do business in another
          state), which can involve additional fees and paperwork.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          2. Tax Environment
        </Layout.Card.Text>
        <Layout.Card.Text>
          States have different tax structures, which can significantly impact
          your bottom line. Some states, like Nevada and Wyoming, do not have a
          corporate income tax, making them attractive for incorporation.
          However, consider all taxes and fees, including franchise taxes and
          other state-specific levies.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          3. Legal Environment
        </Layout.Card.Text>
        <Layout.Card.Text>
          States like Delaware are known for their business-friendly legal
          environment. Delaware has a specialized court, the Court of Chancery,
          which handles corporate law cases efficiently. This can be beneficial
          if you expect to face legal challenges or need swift resolutions.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          4. Privacy Protections
        </Layout.Card.Text>
        <Layout.Card.Text>
          Some states offer greater privacy protections for business owners. For
          example, Nevada and Wyoming provide higher levels of privacy, making
          it harder for individuals to access information about business owners.
        </Layout.Card.Text>
      </StyledBlock>
      <Layout.Divider />
      <StyledBlock id={`${idPrefix}-${2}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Popular States for Incorporation
        </Layout.Card.Text>
        <StyledImageContainer>
          <picture>
            <source
              srcSet={`${config.basePath}/images/blog-delaware.svg`}
              type="image/svg+xml"
            />
            <img
              src={`${config.basePath}/images/blog-delaware.svg`}
              alt="Delaware Map"
            />
          </picture>
        </StyledImageContainer>
        <Layout.Card.Text as="h2" boldest centered>
          Delaware
        </Layout.Card.Text>
        <Layout.Card.Text>
          Delaware is the most popular state for incorporation due to its
          well-developed legal system and business-friendly laws. It offers:
          <ul>
            <li>
              No state corporate income tax for businesses not operating within
              Delaware.
            </li>
            <li>Specialized Court of Chancery for business disputes.</li>
            <li>Greater flexibility in structuring your corporation or LLC.</li>
          </ul>
        </Layout.Card.Text>
        <StyledImageContainer>
          <picture>
            <source
              srcSet={`${config.basePath}/images/blog-nevada.svg`}
              type="image/svg+xml"
            />
            <img
              src={`${config.basePath}/images/blog-nevada.svg`}
              alt="Nevada Map"
            />
          </picture>
        </StyledImageContainer>
        <Layout.Card.Text as="h2" boldest centered>
          Nevada
        </Layout.Card.Text>
        <Layout.Card.Text>
          Nevada is another popular choice due to its lack of state corporate
          income tax and privacy protections for business owners. Benefits
          include:
          <ul>
            <li>No state corporate income tax or franchise tax.</li>
            <li>Strong privacy protections.</li>
            <li>Business-friendly regulatory environment.</li>
          </ul>
        </Layout.Card.Text>
        <StyledImageContainer>
          <picture>
            <source
              srcSet={`${config.basePath}/images/blog-wyoming.svg`}
              type="image/svg+xml"
            />
            <img
              src={`${config.basePath}/images/blog-wyoming.svg`}
              alt="Wyoming Map"
            />
          </picture>
        </StyledImageContainer>
        <Layout.Card.Text as="h2" boldest centered>
          Wyoming
        </Layout.Card.Text>
        <Layout.Card.Text>
          Wyoming is gaining popularity for its low fees and tax advantages. Key
          benefits include:
          <ul>
            <li>No state corporate income tax.</li>
            <li>Low filing and annual fees.</li>
            <li>
              Strong privacy protections and limited disclosure requirements.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <Layout.Divider />
      <StyledBlock id={`${idPrefix}-${3}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Steps to Incorporate in Your Chosen State
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ol>
            <li>
              <b>Choose a Business Structure:</b> Decide whether you want to
              form a corporation, LLC, or another type of entity.
            </li>
            <li>
              <b>Name Your Business:</b> Ensure your business name is available
              in the chosen state and complies with state naming requirements.
            </li>
            <li>
              <b>File Incorporation Documents:</b> Submit the necessary
              formation documents to the state’s Secretary of State office.
            </li>
            <li>
              <b>Obtain Licenses and Permits:</b> Apply for any required state
              and local business licenses and permits.
            </li>
            <li>
              <b>Register for Taxes:</b> Register for state and federal taxes as
              required.
            </li>
            <li>
              <b>Comply with Ongoing Requirements:</b> Stay compliant with state
              requirements, including annual reports and fees.
            </li>
          </ol>
        </Layout.Card.Text>
      </StyledBlock>
      <Layout.Card.Text>
        Choosing the best state to incorporate your business involves
        considering several factors, including taxes, legal environment, and
        costs. While Delaware, Nevada, and Wyoming are popular choices, the best
        state for your business will depend on your specific needs and
        circumstances. By carefully evaluating these factors, you can make an
        informed decision that benefits your business in the long run.
      </Layout.Card.Text>
      <JoinBanner />
      <StyledBackgroundImageContainer>
        <picture>
          <source
            srcSet={`${config.basePath}/images/blog-arrow-logo2.svg`}
            type="image/svg+xml"
          />
          <img
            src={`${config.basePath}/images/blog-arrow-logo2.svg`}
            alt="Background Logo"
          />
        </picture>
      </StyledBackgroundImageContainer>
    </StyledContainer>
  );
};

HowToChooseBestStateToIncorporateBusinessArticle.propTypes = {
  idPrefix: PropTypes.string,
};

export default HowToChooseBestStateToIncorporateBusinessArticle;
